/**
 * Pure helpers for the inline `<badge tone="…">label</badge>` directive.
 *
 * History (PLT-4590): the chat used to ship `[badge:tone]label[/badge]`
 * markers. Models had no pretraining for that syntax and emitted
 * near-misses (`[badge:type=positive]…`, `[badge:tone="positive"]…`)
 * regularly. We migrated to an HTML-like tag because models have orders
 * of magnitude more pretraining for well-formed XML/JSX/MDX. The lax
 * parser is forgiving on attribute quoting / casing / unknown attrs;
 * only `tone` is consulted, and a missing or unknown tone defaults to
 * `neutral`.
 *
 * Marker grammar (canonical):
 *
 *   `<badge tone="positive">+12%</badge>`
 *   `<badge tone="negative">Risk: high</badge>`
 *   `<badge tone="neutral">Stable</badge>`
 *
 * Lax forms also accepted:
 *
 *   `<BADGE TONE=positive>Up</BADGE>`              — case-insensitive
 *   `<badge tone='positive'>Up</badge>`            — single quotes
 *   `<badge tone=“positive”>Up</badge>`            — smart quotes
 *   `<badge tone=positive>Up</badge>`              — unquoted
 *   `<badge>Stable</badge>`                        — defaults tone="neutral"
 *   `<badge tone="warning">Up</badge>`             — unknown tone -> neutral
 *
 * Self-closing or empty-label forms (`<badge />`, `<badge></badge>`,
 * `<badge tone="positive"></badge>`) render nothing — minting a chip
 * with no label would render as an invisible pill.
 *
 * Orphan-opener recovery (PLT-4633): an unclosed `<badge …>` opener (no
 * `</badge>` before a sentence/newline/`<` boundary) used to leak its raw
 * tag text — the server-side `unparseable_badge_tag` leak mode. It is now
 * auto-closed at that boundary and rendered as a chip, with the trailing
 * text preserved. A bare opener with no recoverable label is left visible
 * (Janne's ask in PLT-4633: repair only the confident cases, keep the
 * rest for leak telemetry).
 *
 * Mirrors the citation pattern (see `citation-markers.ts`) — a separate
 * module from the remark plugin so it can be unit-tested under
 * mocha+ts-node without pulling in the ESM-only `unist-util-visit` /
 * `react-markdown` deps.
 */
import type { Tone } from './components/tone';
import { findTagsInText, getAttr, parseTagAttributes } from './html-tag-parser';

export const TONE_BADGE_HREF_PREFIX = 'badge:';

/** Mdast `text` node shape (subset). */
export interface MarkerTextNode {
  type: 'text';
  value: string;
}

/** Mdast `link` node minted to replace each marker (subset). */
export interface MarkerLinkNode {
  type: 'link';
  url: string;
  title: null;
  children: MarkerTextNode[];
}

export type MarkerSplitChild = MarkerTextNode | MarkerLinkNode;

export interface ToneBadge {
  tone: Tone;
  label: string;
}

const VALID_TONES: ReadonlySet<string> = new Set<Tone>(['positive', 'negative', 'neutral']);
const DEFAULT_TONE: Tone = 'neutral';

/**
 * Encode a tone+label pair into the `badge:` URL the remark plugin emits.
 *
 * URL shape: `badge:<tone>|<encodeURIComponent(label)>`. The downstream
 * `getToneBadgeFromHref` reader and the chat's `components.a` override
 * are unchanged from the previous bracket-marker era — only the input
 * grammar changed.
 */
function encodeBadgeHref(tone: Tone, label: string): string {
  return `${TONE_BADGE_HREF_PREFIX}${tone}|${encodeURIComponent(label)}`;
}

/**
 * Resolve the lax-parsed `tone` attribute to a valid `Tone`. Missing,
 * empty, or unknown values fall back to `neutral` rather than failing
 * the parse — agreed with Janne in PLT-4590 ("Skip params that cannot
 * be parsed", with default values for missing required ones).
 *
 * Lower-casing matches the system-prompt vocabulary (positive /
 * negative / neutral). A `Tone="POSITIVE"` attribute parses identically
 * to `tone="positive"`.
 */
function resolveTone(rawTone: string | undefined): Tone {
  if (rawTone === undefined) return DEFAULT_TONE;
  const lowered = rawTone.trim().toLowerCase();
  if (VALID_TONES.has(lowered)) return lowered as Tone;
  return DEFAULT_TONE;
}

/** Mint the `link` node a non-empty tone+label pair renders to. */
function mintBadgeLink(tone: Tone, label: string): MarkerLinkNode {
  return {
    type: 'link',
    url: encodeBadgeHref(tone, label),
    title: null,
    children: [{ type: 'text', value: label }],
  };
}

/**
 * Orphan `<badge …>` opener: a `<badge` open tag with attributes but no
 * trailing `/` (so a well-formed self-closing `<badge … />` is excluded)
 * and — crucially — no matching `</badge>` close in the same string.
 *
 * The `[^/>]*` character class (not `[^>]*`) mirrors the server-side
 * `unparseable_badge_tag` detector (`syntax-leak-detector.ts`): excluding
 * `/` means `<badge tone="…"/>` is left to the well-formed self-closing
 * path rather than being treated as an orphan. Capture group 1 is the
 * attribute source, fed to `parseTagAttributes` so orphan recovery honours
 * the same lax attribute grammar (`tone="…"`, smart quotes, unquoted, …)
 * as the well-formed path.
 */
const ORPHAN_BADGE_OPENER = /<badge\b([^/>]*)>/gi;

/**
 * Find where a recovered orphan-badge label must stop so an unclosed
 * opener can't swallow a whole paragraph into one pill. The label ends at
 * the first of:
 *   - a newline (PLT-4633 scope item 5a),
 *   - a sentence boundary (`.`/`!`/`?` followed by whitespace or
 *     end-of-string, PLT-4633 scope item 5a), or
 *   - a `<` character. Badge labels are plain text and must not contain
 *     tag-like characters (PLT-4633 scope item 5b); stopping at `<` keeps
 *     HTML-looking content out of the label and prevents one orphan
 *     opener from swallowing a following tag as its label.
 * Returns the index in `s` of the first such boundary, or `s.length` when
 * none is found.
 */
function findOrphanLabelBoundary(s: string): number {
  const m = /[\n<]|[.!?](?=\s|$)/.exec(s);
  return m ? m.index : s.length;
}

/**
 * Recover orphan `<badge …>` openers in a span of text that the
 * well-formed parser left untouched. Each opener mints a badge whose
 * label is the text up to the next sentence/newline boundary
 * (`findOrphanLabelBoundary`); the opener tag itself is consumed and the
 * boundary onwards is preserved as following text.
 *
 * Returns `null` when nothing was recovered — including when an opener is
 * present but its label resolves to empty (e.g. a bare `<badge tone="…">`
 * tokenised on its own with no trailing text). In that case the caller
 * keeps the original text verbatim so the unrecoverable opener stays
 * VISIBLE — Janne's explicit ask in PLT-4633: repair only the confident
 * cases, leave the rest visible so leak telemetry keeps flowing.
 */
function recoverOrphanBadges(text: string): MarkerSplitChild[] | null {
  const re = new RegExp(ORPHAN_BADGE_OPENER.source, ORPHAN_BADGE_OPENER.flags);
  const out: MarkerSplitChild[] = [];
  let lastEnd = 0;
  let recovered = false;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const openerEnd = m.index + m[0].length;
    const rest = text.slice(openerEnd);
    const boundary = findOrphanLabelBoundary(rest);
    const label = rest.slice(0, boundary).trim();
    if (label.length === 0) {
      // No recoverable label — leave the opener in place (handled by the
      // trailing-text slice below) and keep scanning past it.
      continue;
    }
    if (m.index > lastEnd) {
      out.push({ type: 'text', value: text.slice(lastEnd, m.index) });
    }
    const tone = resolveTone(getAttr(parseTagAttributes(m[1]), 'tone'));
    out.push(mintBadgeLink(tone, label));
    recovered = true;
    lastEnd = openerEnd + boundary;
    re.lastIndex = lastEnd;
  }
  if (!recovered) return null;
  if (lastEnd < text.length) {
    out.push({ type: 'text', value: text.slice(lastEnd) });
  }
  return out;
}

/**
 * Push a residual text span (one not consumed by a well-formed tag) onto
 * `out`, first attempting orphan-opener recovery. Returns `true` when a
 * badge was recovered from the span, `false` when it was pushed as plain
 * text.
 */
function appendResidualText(out: MarkerSplitChild[], slice: string): boolean {
  const recovered = recoverOrphanBadges(slice);
  if (recovered === null) {
    out.push({ type: 'text', value: slice });
    return false;
  }
  out.push(...recovered);
  return true;
}

/**
 * Split a text string on `<badge …>label</badge>` tags and return the
 * resulting mdast children — text nodes for the spans between tags,
 * link nodes (URL = `badge:<tone>|<encoded>`) for each tag.
 *
 * Well-formed (paired or self-closing) tags parse via `findTagsInText`.
 * Spans the parser leaves untouched are additionally run through
 * `recoverOrphanBadges`, which auto-closes orphan `<badge …>` openers
 * (PLT-4633 scope item 5a) — the `unparseable_badge_tag` leak mode — so
 * an unclosed opener renders as a chip instead of leaking raw tag text.
 *
 * Returns `null` when the string contains no badge tags AND no orphan
 * opener was recovered.
 *
 * Pure function — no AST mutation, no `unist-util-visit` dependency.
 */
export function splitTextOnToneBadgeMarkers(text: string): MarkerSplitChild[] | null {
  const tags = findTagsInText(text, 'badge');

  const newChildren: MarkerSplitChild[] = [];
  let lastEnd = 0;
  // A well-formed tag (even an empty-label one) means we must return a
  // non-null split. Orphan recovery on residual spans can also flip this.
  let producedBadge = tags.length > 0;
  for (const tag of tags) {
    if (tag.start > lastEnd) {
      appendResidualText(newChildren, text.slice(lastEnd, tag.start));
    }
    const tone = resolveTone(getAttr(tag.attrs, 'tone'));
    const label = tag.innerText.trim();
    if (label.length === 0) {
      // Empty/self-closing label — mint nothing rather than an invisible
      // chip. The original tag span is dropped; the surrounding text
      // segments remain. This matches the previous bracket-marker
      // behaviour (which also fell back to plain text on empty labels).
    } else {
      newChildren.push(mintBadgeLink(tone, label));
    }
    lastEnd = tag.end;
  }
  if (lastEnd < text.length) {
    if (appendResidualText(newChildren, text.slice(lastEnd))) {
      producedBadge = true;
    }
  }
  if (!producedBadge) return null;
  // If every match was an empty-label badge that produced no link node,
  // the result might be a single text segment equal to the input minus
  // the swallowed tags. Still return it — the parent splice path expects
  // a non-null array whenever any tag was found.
  return newChildren;
}

/**
 * A badge tag tokenised by micromark as its OWN `html` node value.
 *
 * PLT-5169: the common production case in *inline-only* contexts (table
 * cells especially — they cannot contain block content, so every tag is
 * tokenised inline). When the model emits a paired
 * `<badge tone="positive">high</badge>` there, `remarkParse`/micromark
 * splits it into THREE sibling nodes — `html('<badge tone="positive">')`,
 * `text('high')`, `html('</badge>')`. `splitTextOnToneBadgeMarkers` only
 * runs against a single string, so it matches none of the three: the lone
 * opener html node has no trailing label text in the same node, so even
 * orphan recovery (PLT-4633) returns null, and both tag nodes leak raw to
 * the DOM (we don't run `rehype-raw`). Same root cause as the `<b>`/`<i>`
 * leak fixed in PLT-5054 by `reconstructEmphasisFromHtmlNodes` — this is
 * the badge equivalent of that parent pass.
 *
 * Anchored (`^…$`) — an html node value for a badge tag is exactly the
 * tag itself. The opener's attr capture uses `[^/>]*` (not `[^>]*`),
 * mirroring the server-side `unparseable_badge_tag` detector and the
 * orphan-opener regex above: excluding `/` keeps a self-closing
 * `<badge … />` out of the opener branch.
 */
const HTML_BADGE_OPEN_RE = /^<badge\b([^/>]*)>\s*$/i;
const HTML_BADGE_CLOSE_RE = /^<\/badge\s*>\s*$/i;
const HTML_BADGE_SELF_CLOSE_RE = /^<badge\b[^>]*\/>\s*$/i;

/**
 * Minimal mdast node shape the reconstruction needs to inspect: a `type`,
 * an optional `value` (html / text nodes), and optional `children`
 * (container nodes). Same shape as `EmphasisReconNode` in
 * `inline-emphasis-tags.ts`.
 */
export interface BadgeReconNode {
  type: string;
  value?: string;
  children?: BadgeReconNode[];
}

/**
 * Flatten a slice of sibling nodes into the plain-text badge label, or
 * return `null` when the slice contains a node we can't confidently
 * textify. Badge labels are plain text by contract (the minted link node
 * carries a single text child), so inline formatting the parser already
 * converted inside the label (`high *severity*` → `emphasis`) is
 * flattened to its text content. An `html` node (e.g. a stray tag) or a
 * value-less leaf makes the label non-confident — bail so the
 * reconstruction leaves the whole span untouched for leak telemetry,
 * matching the "repair only the confident cases" contract (PLT-4633).
 */
function collectBadgeLabelText(nodes: readonly BadgeReconNode[]): string | null {
  let label = '';
  for (const node of nodes) {
    if (node.type === 'html') return null;
    if (node.type === 'text' && typeof node.value === 'string') {
      label += node.value;
      continue;
    }
    if (Array.isArray(node.children)) {
      const inner = collectBadgeLabelText(node.children);
      if (inner === null) return null;
      label += inner;
      continue;
    }
    return null;
  }
  return label;
}

/**
 * Reconstruct `<badge tone="…">label</badge>` directives that micromark
 * split across sibling `html` / `text` nodes (PLT-5169).
 *
 * Walks one parent's `children` array left-to-right. When an `html` node
 * is a badge opener, scans forward for the closing `</badge>` `html`
 * node, then replaces the open…close range with the minted badge link
 * node (URL = `badge:<tone>|<encoded>`), exactly as
 * `splitTextOnToneBadgeMarkers` would have for the unsplit string.
 * Siblings after the closer (e.g. the ` (79)` in
 * `<badge …>high</badge> (79)`) are untouched and keep rendering as
 * regular content. Badges don't nest, so the first closer wins; a second
 * opener before any closer marks the span malformed and leaves it raw.
 *
 * Empty-label paired forms collapse to nothing and self-closing html
 * forms are dropped — minting an invisible chip would be worse than
 * nothing, matching `splitTextOnToneBadgeMarkers`. An unclosed opener or
 * a lone closer is left untouched (visible) so the syntax-leak telemetry
 * keeps flowing.
 *
 * Returns `null` when nothing changed so the remark plugin can skip the
 * splice. Pure function (no AST mutation, generic over the node shape) so
 * it can be unit-tested directly, matching the rest of this module.
 */
export function reconstructBadgeFromHtmlNodes(children: readonly BadgeReconNode[]): BadgeReconNode[] | null {
  let changed = false;
  const out: BadgeReconNode[] = [];
  let i = 0;
  while (i < children.length) {
    const node = children[i];
    if (node.type === 'html' && typeof node.value === 'string') {
      // Self-closing `<badge … />` carries no label — drop it.
      if (HTML_BADGE_SELF_CLOSE_RE.test(node.value)) {
        changed = true;
        i += 1;
        continue;
      }
      const open = HTML_BADGE_OPEN_RE.exec(node.value);
      if (open) {
        // Find the closing `</badge>` among the following siblings.
        // Badges don't nest — another opener (or self-closing badge)
        // before the closer means the span is malformed; bail and leave
        // it raw for telemetry.
        let closeIdx = -1;
        for (let j = i + 1; j < children.length; j++) {
          const sibling = children[j];
          if (sibling.type !== 'html' || typeof sibling.value !== 'string') continue;
          if (HTML_BADGE_CLOSE_RE.test(sibling.value)) {
            closeIdx = j;
            break;
          }
          if (HTML_BADGE_OPEN_RE.test(sibling.value) || HTML_BADGE_SELF_CLOSE_RE.test(sibling.value)) {
            break;
          }
        }
        if (closeIdx !== -1) {
          const label = collectBadgeLabelText(children.slice(i + 1, closeIdx));
          if (label !== null) {
            const trimmed = label.trim();
            if (trimmed.length > 0) {
              const tone = resolveTone(getAttr(parseTagAttributes(open[1].trim()), 'tone'));
              out.push(mintBadgeLink(tone, trimmed) as unknown as BadgeReconNode);
            }
            // Empty label (`<badge></badge>`) collapses to nothing —
            // tags consumed, no invisible chip minted.
            changed = true;
            i = closeIdx + 1;
            continue;
          }
          // Un-textifiable inner content — leave the span untouched.
        }
        // No matching closer — leave the opener untouched (unclosed contract).
      }
    }
    out.push(node);
    i += 1;
  }
  return changed ? out : null;
}

/**
 * If `href` is a tone-badge marker URL minted by `remarkToneBadges`,
 * return the parsed `{tone, label}` pair. Otherwise return null.
 *
 * Unchanged across the PLT-4590 input-grammar migration — the URL
 * scheme is the integration boundary between the remark plugin and the
 * chat-side `components.a` override, and changing it would force a
 * coordinated touch on every consumer.
 *
 * Percent-encoded separator (PLT-5169): the minted URL uses a literal
 * `|` separator, but react-markdown v10's mdast→hast step runs every
 * link url through `micromark-util-sanitize-uri`'s `normalizeUri`,
 * whose safe-character set excludes `|` — so by the time the
 * `components.a` override sees the href, the separator is `%7C`. The
 * `cite:` / `lock:` schemes only carry safe characters and are
 * unaffected; badge is the only scheme with `|`. Accept both forms
 * here (the first separator wins, matching the literal-`|` path; the
 * label was `encodeURIComponent`-ed at mint time so an encoded pipe
 * inside the label always comes AFTER the separator). Without this,
 * every badge chip silently degrades to a plain link even when the
 * remark plugin minted the link node correctly.
 */
export function getToneBadgeFromHref(href: string | undefined | null): ToneBadge | null {
  if (typeof href !== 'string') return null;
  if (!href.startsWith(TONE_BADGE_HREF_PREFIX)) return null;
  const payload = href.slice(TONE_BADGE_HREF_PREFIX.length);
  let sepIdx = payload.indexOf('|');
  let sepLen = 1;
  if (sepIdx === -1) {
    sepIdx = payload.toLowerCase().indexOf('%7c');
    sepLen = 3;
  }
  if (sepIdx === -1) return null;
  const tone = payload.slice(0, sepIdx);
  if (tone !== 'positive' && tone !== 'negative' && tone !== 'neutral') return null;
  try {
    const label = decodeURIComponent(payload.slice(sepIdx + sepLen));
    if (label.length === 0) return null;
    return { tone, label };
  } catch {
    return null;
  }
}

/**
 * Build a `react-markdown` `urlTransform` that lets `badge:<tone>|<encoded>`
 * URLs through unchanged.
 *
 * Compose with the existing citation transform — see `upright-chat.tsx`
 * for the composition order.
 */
export function makeToneBadgeAwareUrlTransform(defaultTransform: (url: string) => string): (url: string) => string {
  return url => {
    if (url.startsWith(TONE_BADGE_HREF_PREFIX)) return url;
    return defaultTransform(url);
  };
}
